import * as XLSX from 'xlsx';

export const readExcelFile = async (filePath) => {
  try {
    const response = await fetch(filePath);
    const arrayBuffer = await response.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: 'array' });
    
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);
    
    return jsonData;
  } catch (error) {
    console.error('Error reading Excel file:', error);
    return [];
  }
};

// Helper function to parse Excel dates
const parsePickingDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // Handle Excel date serial numbers
    if (typeof dateValue === 'number') {
      // Excel stores dates as numbers (days since 1/1/1900)
      // Need to subtract 25569 to convert to Unix timestamp
      const date = new Date((dateValue - 25569) * 86400 * 1000);
      // Validate the date is reasonable (between 2020 and 2030)
      const year = date.getFullYear();
      if (year >= 2020 && year <= 2030) {
        return date;
      }
    }
    
    // Handle date strings
    if (typeof dateValue === 'string') {
      // Try direct parsing first
      let date = new Date(dateValue);
      
      // If that fails, try common date formats
      if (isNaN(date.getTime())) {
        // Try MM/DD/YYYY format
        const parts = dateValue.split(/[\/\-]/);
        if (parts.length === 3) {
          // Try MM/DD/YYYY
          date = new Date(parts[2], parts[0] - 1, parts[1]);
          if (isNaN(date.getTime())) {
            // Try DD/MM/YYYY
            date = new Date(parts[2], parts[1] - 1, parts[0]);
          }
        }
      }
      
      // Validate the date
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        if (year >= 2020 && year <= 2030) {
          return date;
        }
      }
    }
  } catch (error) {
    console.error('Error parsing date:', dateValue, error);
  }
  
  return null;
};

export const processDeliveryData = (atlData, obdData, newData) => {
  console.log('Processing delivery data...');
  console.log('ATL data rows:', atlData ? atlData.length : 0);
  console.log('OBD data rows:', obdData ? obdData.length : 0);
  console.log('New data rows (6/29-7/29):', newData ? newData.length : 0);
  
  // Log sample data to debug
  if (atlData && atlData.length > 0) {
    console.log('ATL sample row:', atlData[0]);
    console.log('ATL Picking Date value:', atlData[0]['Picking Date']);
  }
  if (obdData && obdData.length > 0) {
    console.log('OBD sample row:', obdData[0]);
    console.log('OBD Picking Date value:', obdData[0]['Picking Date']);
  }
  if (newData && newData.length > 0) {
    console.log('New data sample row:', newData[0]);
    console.log('New data Picking Date value:', newData[0]['Picking Date']);
  }
  
  // Process ATL data with ATL platform designation
  const processedATLData = (atlData || []).map(row => {
    const parsedDate = parsePickingDate(row['Picking Date']);
    return {
      deliveryNumber: row['Delivery'] || row['OBD'],
      pickingDate: parsedDate,
      pickingDateString: parsedDate ? parsedDate.toISOString().split('T')[0] : null,
      routeName: row['Route Name'],
      store: row['Store'],
      storeName: row['Store Name'],
      item: row['Item'],
      itemDescription: row['Item Description'] || row['Material Description'],
      sku: row['Material'] || row['SKU'],
      materialDescription: row['Item Description'] || row['Material Description'],
      materialGroup: row['Material Group'],
      qtyOrder: parseFloat(row['Qty Order'] || 0),
      qtyPick: parseFloat(row['Qty Pick'] || 0),
      uom: row['UoM'],
      fulfillmentRate: row['Qty Order'] ? (parseFloat(row['Qty Pick'] || 0) / parseFloat(row['Qty Order'])) : 0,
      platform: 'ATL' // Force ATL platform for ATL data file
    };
  });
  
  // Process OBD data normally (non-ATL platforms)
  const processedOBDData = (obdData || []).map(row => {
    const parsedDate = parsePickingDate(row['Picking Date']);
    return {
      deliveryNumber: row['Delivery'] || row['OBD'],
      pickingDate: parsedDate,
      pickingDateString: parsedDate ? parsedDate.toISOString().split('T')[0] : null,
      routeName: row['Route Name'],
      store: row['Store'],
      storeName: row['Store Name'],
      item: row['Item'],
      itemDescription: row['Item Description'] || row['Material Description'],
      sku: row['Material'] || row['SKU'],
      materialDescription: row['Item Description'] || row['Material Description'],
      materialGroup: row['Material Group'],
      qtyOrder: parseFloat(row['Qty Order'] || 0),
      qtyPick: parseFloat(row['Qty Pick'] || 0),
      uom: row['UoM'],
      fulfillmentRate: row['Qty Order'] ? (parseFloat(row['Qty Pick'] || 0) / parseFloat(row['Qty Order'])) : 0,
      platform: null // Will be mapped using MDM
    };
  });
  
  // Process new data (6/29-7/29) - treat like OBD data as it contains all platforms
  const processedNewData = (newData || []).map(row => {
    const parsedDate = parsePickingDate(row['Picking Date']);
    return {
      deliveryNumber: row['Delivery'] || row['OBD'],
      pickingDate: parsedDate,
      pickingDateString: parsedDate ? parsedDate.toISOString().split('T')[0] : null,
      routeName: row['Route Name'],
      store: row['Store'],
      storeName: row['Store Name'],
      item: row['Item'],
      itemDescription: row['Item Description'] || row['Material Description'],
      sku: row['Material'] || row['SKU'],
      materialDescription: row['Item Description'] || row['Material Description'],
      materialGroup: row['Material Group'],
      qtyOrder: parseFloat(row['Qty Order'] || 0),
      qtyPick: parseFloat(row['Qty Pick'] || 0),
      uom: row['UoM'],
      fulfillmentRate: row['Qty Order'] ? (parseFloat(row['Qty Pick'] || 0) / parseFloat(row['Qty Order'])) : 0,
      platform: null // Will be mapped using MDM
    };
  });
  
  // Combine all three datasets and filter out invalid dates
  const combinedData = [...processedATLData, ...processedOBDData, ...processedNewData].filter(row => row.pickingDate !== null);
  
  console.log('=== DELIVERY DATA PROCESSING DEBUG ===');
  console.log('Total rows processed:', combinedData.length);
  console.log('ATL data (with embedded platform):', processedATLData.length);
  console.log('OBD data (needs platform mapping):', processedOBDData.length);
  console.log('New data (needs platform mapping):', processedNewData.length);
  
  if (combinedData.length > 0) {
    console.log('Sample delivery records BEFORE platform assignment:');
    combinedData.slice(0, 5).forEach((item, i) => {
      console.log('Record ' + i + ':', {
        store: item.store,
        storeType: typeof item.store,
        platform: item.platform,
        deliveryNumber: item.deliveryNumber
      });
    });
    
    // Show unique stores in delivery data
    const uniqueStores = [...new Set(combinedData.map(item => String(item.store)))];
    console.log('Unique stores in delivery data:', uniqueStores.slice(0, 20));
    console.log('Total unique stores:', uniqueStores.length);
  }
  
  return combinedData;
};

export const processMDMData = (mdmData) => {
  const platformMapping = {};
  const storeNameMapping = {};
  
  console.log('=== MDM PROCESSING DEBUG ===');
  console.log('MDM rows:', mdmData.length);
  
  if (mdmData.length > 0) {
    console.log('First MDM row keys:', Object.keys(mdmData[0]));
    console.log('First 3 MDM rows:');
    mdmData.slice(0, 3).forEach((row, i) => {
      console.log('Row ' + i + ':', {
        columnA_EMPTY: row['__EMPTY'],
        columnF_SAPSearch: row['SAP Search 1'],
        columnD_StoreName: row['SAP_2'],
        columnJ_PLSC: row['PLSC'],
        allKeys: Object.keys(row)
      });
    });
  }
  
  mdmData.forEach((row, index) => {
    const storeWithoutZeros = row['__EMPTY'];
    const storeWithZeros = row['SAP Search 1'];
    const platform = row['PLSC'];
    const storeName = row['SAP_2'];
    
    if (index < 5) {
      console.log('Processing row ' + index + ':', {
        storeWithoutZeros,
        storeWithZeros,
        platform,
        storeName
      });
    }
    
    if (platform && platform !== 'N/A' && platform !== 'STORE') {
      if (storeWithoutZeros) {
        const storeKey = String(storeWithoutZeros);
        platformMapping[storeKey] = platform;
        if (storeName) storeNameMapping[storeKey] = storeName;
        
        if (index < 5) {
          console.log('Added mapping (A): ' + storeKey + ' -> ' + platform);
        }
      }
      
      if (storeWithZeros) {
        const storeKey = String(storeWithZeros);
        platformMapping[storeKey] = platform;
        if (storeName) storeNameMapping[storeKey] = storeName;
        
        const storeKeyNoZeros = storeKey.replace(/^0+/, '') || storeKey;
        platformMapping[storeKeyNoZeros] = platform;
        if (storeName) storeNameMapping[storeKeyNoZeros] = storeName;
        
        if (index < 5) {
          console.log('Added mapping (F): ' + storeKey + ' and ' + storeKeyNoZeros + ' -> ' + platform);
        }
      }
    }
  });
  
  console.log('=== MDM PROCESSING COMPLETE ===');
  console.log('Total store mappings:', Object.keys(platformMapping).length);
  console.log('Unique platforms:', [...new Set(Object.values(platformMapping))]);
  console.log('Sample mappings:', Object.entries(platformMapping).slice(0, 10));
  
  return { platformMapping, storeNameMapping };
};

export const aggregateByPlatform = (deliveryData, platformMapping) => {
  const platformStats = {};
  const unmappedStores = new Set();
  const mappedStores = new Set();
  
  console.log('=== PLATFORM AGGREGATION DEBUG ===');
  console.log('Input deliveryData type:', typeof deliveryData);
  console.log('Input deliveryData is array:', Array.isArray(deliveryData));
  console.log('Delivery data rows:', deliveryData ? deliveryData.length : 'undefined');
  console.log('Platform mapping type:', typeof platformMapping);
  console.log('Platform mapping keys available:', platformMapping ? Object.keys(platformMapping).length : 'undefined');
  
  if (!deliveryData || !Array.isArray(deliveryData)) {
    console.error('CRITICAL: deliveryData is not an array!', deliveryData);
    return {};
  }
  
  if (!platformMapping) {
    console.error('CRITICAL: platformMapping is undefined!');
    return {};
  }
  
  console.log('Sample platform mapping:', Object.entries(platformMapping).slice(0, 10));
  
  if (deliveryData.length > 0) {
    console.log('Sample delivery data stores:', deliveryData.slice(0, 10).map(item => ({
      store: item.store,
      storeType: typeof item.store,
      platform: item.platform
    })));
  }
  
  deliveryData.forEach((item, index) => {
    // Use embedded platform if available (for ATL), otherwise use mapping
    let platform = item.platform;
    
    if (!platform) {
      // Try to match store number from data with MDM mapping
      const storeNumber = String(item.store);
      platform = platformMapping[storeNumber];
      
      if (platform) {
        mappedStores.add(storeNumber);
      } else if (index < 10) {
        console.log('No mapping found for store: ' + storeNumber + ' (type: ' + typeof item.store + ')');
      }
    }
    
    if (!platform) {
      platform = 'Unknown';
      unmappedStores.add(item.store);
    }
    
    if (index < 10) {
      console.log('Item ' + index + ' - Store: ' + item.store + ' -> Platform: ' + platform);
    }
    
    
    if (!platformStats[platform]) {
      platformStats[platform] = {
        totalOrders: 0,
        totalQtyOrdered: 0,
        totalQtyPicked: 0,
        stores: new Set(),
        products: {},
        dailyVolumes: {}
      };
    }
    
    platformStats[platform].totalOrders++;
    platformStats[platform].totalQtyOrdered += item.qtyOrder;
    platformStats[platform].totalQtyPicked += item.qtyPick;
    platformStats[platform].stores.add(item.store);
    
    // Track products
    const productKey = item.sku;
    if (!platformStats[platform].products[productKey]) {
      platformStats[platform].products[productKey] = {
        sku: item.sku,
        description: item.materialDescription,
        totalQtyOrdered: 0,
        totalQtyPicked: 0
      };
    }
    platformStats[platform].products[productKey].totalQtyOrdered += item.qtyOrder;
    platformStats[platform].products[productKey].totalQtyPicked += item.qtyPick;
    
    // Track daily volumes using pickingDateString
    const date = item.pickingDateString || 'Unknown';
    if (!platformStats[platform].dailyVolumes[date]) {
      platformStats[platform].dailyVolumes[date] = 0;
    }
    platformStats[platform].dailyVolumes[date]++;
  });
  
  // Calculate fulfillment rates and convert sets to arrays
  Object.keys(platformStats).forEach(platform => {
    platformStats[platform].fulfillmentRate = platformStats[platform].totalQtyOrdered > 0
      ? platformStats[platform].totalQtyPicked / platformStats[platform].totalQtyOrdered
      : 0;
    platformStats[platform].stores = Array.from(platformStats[platform].stores);
    platformStats[platform].storeCount = platformStats[platform].stores.length;
  });
  
  console.log('=== PLATFORM AGGREGATION COMPLETE ===');
  console.log('Final platformStats object keys:', Object.keys(platformStats));
  console.log('Raw platformStats object structure:', JSON.stringify(platformStats, null, 2));
  console.log('Platform order counts:', Object.entries(platformStats).map(([p, s]) => p + ': ' + s.totalOrders));
  
  console.log('Successfully mapped stores:', mappedStores.size);
  console.log('Sample mapped stores:', Array.from(mappedStores).slice(0, 10));
  
  if (unmappedStores.size > 0) {
    console.log('Unmapped stores (showing as Unknown):', Array.from(unmappedStores).slice(0, 20));
    console.log('Total unmapped stores:', unmappedStores.size);
  }
  
  // Validate the final object structure
  Object.entries(platformStats).forEach(([platform, stats]) => {
    console.log('Platform ' + platform + ':', {
      totalOrders: stats.totalOrders,
      storeCount: stats.stores ? stats.stores.length : 'no stores array',
      hasProducts: !!stats.products,
      hasDailyVolumes: !!stats.dailyVolumes
    });
  });
  
  return platformStats;
};

export const aggregateByStore = (deliveryData) => {
  const storeStats = {};
  
  deliveryData.forEach(item => {
    const store = item.store;
    
    if (!storeStats[store]) {
      storeStats[store] = {
        storeName: item.storeName,
        totalOrders: 0,
        totalQtyOrdered: 0,
        totalQtyPicked: 0,
        materialGroups: new Set()
      };
    }
    
    storeStats[store].totalOrders++;
    storeStats[store].totalQtyOrdered += item.qtyOrder;
    storeStats[store].totalQtyPicked += item.qtyPick;
    storeStats[store].materialGroups.add(item.materialGroup);
  });
  
  // Calculate fulfillment rates
  Object.keys(storeStats).forEach(store => {
    storeStats[store].fulfillmentRate = storeStats[store].totalQtyOrdered > 0
      ? storeStats[store].totalQtyPicked / storeStats[store].totalQtyOrdered
      : 0;
    storeStats[store].materialGroupCount = storeStats[store].materialGroups.size;
  });
  
  return storeStats;
};

export const getOrderTrends = (deliveryData) => {
  const trends = {};
  
  deliveryData.forEach(item => {
    const date = item.pickingDateString || 'Unknown';
    
    if (date !== 'Unknown' && !trends[date]) {
      trends[date] = {
        date,
        orderCount: 0,
        totalQtyOrdered: 0,
        totalQtyPicked: 0
      };
    }
    
    if (date !== 'Unknown') {
      trends[date].orderCount++;
      trends[date].totalQtyOrdered += item.qtyOrder;
      trends[date].totalQtyPicked += item.qtyPick;
    }
  });
  
  // Convert to array and sort by date
  return Object.values(trends).sort((a, b) => {
    return new Date(a.date) - new Date(b.date);
  });
};

export const getTopProducts = (products, limit = 10) => {
  return Object.values(products)
    .sort((a, b) => b.totalQtyOrdered - a.totalQtyOrdered)
    .slice(0, limit);
};

export const getTopStores = (storeStats, platformMapping, storeNameMapping, limit = 10) => {
  return Object.entries(storeStats)
    .map(([store, stats]) => ({
      store,
      platform: platformMapping[store] || 'Unknown',
      storeNameFromMDM: storeNameMapping[store] || null,
      ...stats
    }))
    .sort((a, b) => b.totalOrders - a.totalOrders)
    .slice(0, limit);
};

export const getMaterialGroupPerformance = (deliveryData) => {
  const groupStats = {};
  
  deliveryData.forEach(item => {
    const group = item.materialGroup || 'Unknown';
    
    if (!groupStats[group]) {
      groupStats[group] = {
        name: group,
        totalQtyOrdered: 0,
        totalQtyPicked: 0,
        orderCount: 0
      };
    }
    
    groupStats[group].orderCount++;
    groupStats[group].totalQtyOrdered += item.qtyOrder;
    groupStats[group].totalQtyPicked += item.qtyPick;
  });
  
  // Calculate fulfillment rates
  Object.values(groupStats).forEach(group => {
    group.fulfillmentRate = group.totalQtyOrdered > 0
      ? group.totalQtyPicked / group.totalQtyOrdered
      : 0;
  });
  
  return Object.values(groupStats);
};

// Helper function to format dates consistently
export const formatDate = (dateValue) => {
  if (!dateValue) return null;
  
  // If it's already a Date object
  if (dateValue instanceof Date) {
    return dateValue.toISOString().split('T')[0];
  }
  
  // If it's a string, try to parse it
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

// Get daily quantity picked for line graph
export const getDailyQuantityPicked = (deliveryData) => {
  const dailyData = {};
  
  deliveryData.forEach(item => {
    const date = item.pickingDateString;
    if (!date) return;
    
    if (!dailyData[date]) {
      dailyData[date] = {
        date,
        totalQtyPicked: 0
      };
    }
    
    dailyData[date].totalQtyPicked += item.qtyPick;
  });
  
  return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Get weekly quantity by store
export const getWeeklyQuantityByStore = (deliveryData) => {
  const weeklyData = {};
  
  deliveryData.forEach(item => {
    if (!item.pickingDate) return;
    
    // Get week start date (Monday)
    const d = new Date(item.pickingDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    const weekStart = d.toISOString().split('T')[0];
    
    const key = weekStart + '_' + item.store;
    
    if (!weeklyData[key]) {
      weeklyData[key] = {
        weekStart,
        store: item.store,
        storeName: item.storeName || ('Store ' + item.store),
        totalQtyPicked: 0
      };
    }
    
    weeklyData[key].totalQtyPicked += item.qtyPick;
  });
  
  // Group by week for charting
  const weeklyGrouped = {};
  Object.values(weeklyData).forEach(data => {
    if (!weeklyGrouped[data.weekStart]) {
      weeklyGrouped[data.weekStart] = [];
    }
    weeklyGrouped[data.weekStart].push(data);
  });
  
  return weeklyGrouped;
};

// Get daily picked quantity by platform
export const getDailyPickedByPlatform = (deliveryData, platformMapping) => {
  const dailyPlatformData = {};
  
  deliveryData.forEach(item => {
    const date = item.pickingDateString;
    if (!date) return;
    
    const platform = item.platform || platformMapping[item.store] || 'Unknown';
    const key = date + '_' + platform;
    
    if (!dailyPlatformData[key]) {
      dailyPlatformData[key] = {
        date,
        platform,
        qtyPicked: 0
      };
    }
    
    dailyPlatformData[key].qtyPicked += item.qtyPick;
  });
  
  // Transform for multi-line chart
  const dateMap = {};
  Object.values(dailyPlatformData).forEach(data => {
    if (!dateMap[data.date]) {
      dateMap[data.date] = { date: data.date };
    }
    dateMap[data.date][data.platform] = data.qtyPicked;
  });
  
  const result = Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Debug logging
  const platforms = [...new Set(Object.values(dailyPlatformData).map(d => d.platform))];
  console.log('Platforms in daily data:', platforms);
  
  return result;
};

// Get fill rate over time with trend
export const getFillRateOverTime = (deliveryData) => {
  const dailyData = {};
  
  deliveryData.forEach(item => {
    const date = item.pickingDateString;
    if (!date) return;
    
    if (!dailyData[date]) {
      dailyData[date] = {
        date,
        totalQtyOrdered: 0,
        totalQtyPicked: 0
      };
    }
    
    dailyData[date].totalQtyOrdered += item.qtyOrder;
    dailyData[date].totalQtyPicked += item.qtyPick;
  });
  
  // Calculate fill rates
  const fillRateData = Object.values(dailyData)
    .filter(day => day.totalQtyOrdered > 0) // Only include days with orders
    .map(day => ({
      date: day.date,
      fillRate: (day.totalQtyPicked / day.totalQtyOrdered) * 100
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Calculate linear regression for trendline
  const n = fillRateData.length;
  if (n < 2) return { fillRateData, trendData: [] };
  
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  
  fillRateData.forEach((point, index) => {
    sumX += index;
    sumY += point.fillRate;
    sumXY += index * point.fillRate;
    sumX2 += index * index;
  });
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line data
  const trendData = fillRateData.map((point, index) => ({
    date: point.date,
    trend: Math.max(0, Math.min(100, slope * index + intercept)) // Keep between 0-100
  }));
  
  console.log('Fill rate data points:', fillRateData.length);
  console.log('Average fill rate:', (sumY / n).toFixed(2) + '%');
  
  return { fillRateData, trendData };
};

// Get average lines per weekday by platform
export const getAverageLinesByWeekdayPlatform = (deliveryData, platformMapping) => {
  const weekdayPlatformData = {};
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  deliveryData.forEach(item => {
    if (!item.pickingDate) return;
    
    const d = new Date(item.pickingDate);
    const weekday = weekdays[d.getDay()];
    const platform = item.platform || platformMapping[item.store] || 'Unknown';
    const key = weekday + '_' + platform;
    
    if (!weekdayPlatformData[key]) {
      weekdayPlatformData[key] = {
        weekday,
        platform,
        totalLines: 0,
        days: new Set()
      };
    }
    
    weekdayPlatformData[key].totalLines++;
    weekdayPlatformData[key].days.add(item.pickingDateString);
  });
  
  // Calculate averages
  const averageData = {};
  Object.values(weekdayPlatformData).forEach(data => {
    const avgKey = data.platform + '_' + data.weekday;
    averageData[avgKey] = {
      platform: data.platform,
      weekday: data.weekday,
      averageLines: data.totalLines / data.days.size
    };
  });
  
  // Transform for heatmap
  const platforms = [...new Set(Object.values(averageData).map(d => d.platform))].sort();
  const heatmapData = platforms.map(platform => {
    const row = { platform };
    weekdays.forEach(weekday => {
      const key = platform + '_' + weekday;
      row[weekday] = averageData[key] ? averageData[key].averageLines : 0;
    });
    return row;
  });
  
  return { heatmapData, weekdays, platforms };
};

// Get top items by fill rate
export const getTopItemsByFillRate = (deliveryData, limit = 20) => {
  const itemStats = {};
  
  deliveryData.forEach(item => {
    const key = item.itemDescription || item.materialDescription || item.sku;
    if (!key) return;
    
    if (!itemStats[key]) {
      itemStats[key] = {
        itemDescription: key,
        totalQtyOrdered: 0,
        totalQtyPicked: 0,
        orderCount: 0
      };
    }
    
    itemStats[key].totalQtyOrdered += item.qtyOrder;
    itemStats[key].totalQtyPicked += item.qtyPick;
    itemStats[key].orderCount++;
  });
  
  // Calculate fill rates and filter
  const itemsWithFillRate = Object.values(itemStats)
    .filter(item => item.totalQtyOrdered > 0) // Only items with orders
    .map(item => ({
      ...item,
      fillRate: (item.totalQtyPicked / item.totalQtyOrdered) * 100
    }))
    .sort((a, b) => b.fillRate - a.fillRate) // Sort by fill rate descending
    .slice(0, limit);
  
  return itemsWithFillRate;
};

// Get weekly trends overview
export const getWeeklyTrends = (deliveryData) => {
  const weeklyData = {};
  
  deliveryData.forEach(item => {
    if (!item.pickingDate) return;
    
    // Get week start date (Monday)
    const d = new Date(item.pickingDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    const weekStart = d.toISOString().split('T')[0];
    
    if (!weeklyData[weekStart]) {
      weeklyData[weekStart] = {
        weekStart,
        orderCount: 0,
        totalQtyOrdered: 0,
        totalQtyPicked: 0
      };
    }
    
    weeklyData[weekStart].orderCount++;
    weeklyData[weekStart].totalQtyOrdered += item.qtyOrder;
    weeklyData[weekStart].totalQtyPicked += item.qtyPick;
  });
  
  // Convert to array and calculate fill rates
  const weeklyTrends = Object.values(weeklyData)
    .map(week => ({
      ...week,
      fillRate: week.totalQtyOrdered > 0 
        ? (week.totalQtyPicked / week.totalQtyOrdered) * 100 
        : 0,
      weekLabel: new Date(week.weekStart).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })
    }))
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart))
    .slice(-12); // Show last 12 weeks
  
  return weeklyTrends;
};